import React, { createContext, useState, useCallback, ReactNode } from 'react';
import { Alert } from '../components/common/Alert';
import { AppNotification } from '../types';
import { errorService } from '../services/error';

interface NotificationContextType {
  notifications: AppNotification[];
  unreadCount: number;
  setNotifications: React.Dispatch<React.SetStateAction<AppNotification[]>>;
  setUnreadCount: React.Dispatch<React.SetStateAction<number>>;
  addNotification: (message: string, type: AppNotification['type'], options?: Partial<AppNotification>) => void;
  removeNotification: (id: string) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearNotification: (id: string) => void;
  clearAllNotifications: () => void;
  showError: (error: Error | unknown, context?: Record<string, any>) => void;
}

export const NotificationContext = createContext<NotificationContextType | null>(null);

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const addNotification = useCallback((message: string, type: AppNotification['type'] = 'info', options: Partial<AppNotification> = {}) => {
    const newNotification: AppNotification = {
      id: `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type,
      message,
      createdAt: new Date(),
      read: false,
      ...options,
    };

    setNotifications((prev) => [newNotification, ...prev]);
    if (!newNotification.read) {
      setUnreadCount((prev) => prev + 1);
    }

    // Auto-remove toast notifications after delay
    if (!newNotification.actionUrl) {
      setTimeout(() => {
        removeNotification(newNotification.id);
      }, 5000);
    }
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((notification) => notification.id !== id));
  }, []);

  const markAsRead = useCallback((id: string) => {
    setNotifications((prev) => prev.map((notification) => (notification.id === id ? { ...notification, read: true } : notification)));
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => prev.map((notification) => ({ ...notification, read: true })));
  }, []);

  const clearNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((notification) => notification.id !== id));
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const showError = useCallback(
    (error: Error | unknown, context?: Record<string, any>) => {
      const errorDetails = errorService.handleError(error, context);

      addNotification(errorDetails.message, 'error', {
        title: 'حدث خطأ',
        priority: 'high',
      });

      return errorDetails;
    },
    [addNotification],
  );

  const value = {
    notifications,
    unreadCount,
    setNotifications,
    setUnreadCount,
    addNotification,
    removeNotification,
    markAsRead,
    markAllAsRead,
    clearNotification,
    clearAllNotifications,
    showError,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <div
        className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-3 max-w-full sm:max-w-xs md:max-w-md w-auto px-2 sm:px-0 pointer-events-none"
        style={{ direction: 'rtl' }} // يدعم RTL تلقائياً إذا كان الموقع RTL
      >
        {notifications
          .filter((n) => !n.actionUrl)
          .map((notification) => (
            <div className="pointer-events-auto w-full max-w-xs sm:max-w-xs md:max-w-md">
              <Alert key={notification.id} type={notification.type} message={notification.message} onClose={() => removeNotification(notification.id)} variant="toast" />
            </div>
          ))}
      </div>
    </NotificationContext.Provider>
  );
};
